import React, { useState, useEffect } from 'react';
import { Search, Calendar, User, ArrowLeft, Bookmark, Share2, Eye, Clock, Save, Edit, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import { api } from '../services/api';
import { SiteContent, ArticlesContent, Article } from '../types';
import { useAuth } from '../context/AuthContext';
import EditableContent from '../components/Admin/EditableContent';

const ArticlesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('همه');

  useEffect(() => {
    const fetchData = async () => {
      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateArticlesContent = (section: keyof ArticlesContent, key: string, value: any) => {
    if (!content) return;
    setContent({
      ...content,
      articles: {
        ...content.articles,
        [section]: typeof content.articles[section] === 'object' && !Array.isArray(content.articles[section])
          ? { ...(content.articles[section] as any), [key]: value }
          : value
      }
    });
  };

  const updateArticleItem = (index: number, field: keyof Article, value: any) => {
    if (!content) return;
    const newArticles = [...content.articles.articles];
    newArticles[index] = { ...newArticles[index], [field]: value };
    setContent({
        ...content,
        articles: {
            ...content.articles,
            articles: newArticles
        }
    });
  };

  if (!content || !content.articles) return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;

  const isAdmin = user?.role === 'admin';
  const { articles } = content;

  const filteredArticles = articles.articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'همه' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-gray-50 min-h-screen pb-20 relative">
      {/* Admin Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
            {isEditing ? (
                <>
                    <Button onClick={handleSaveContent} disabled={saving} className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2">
                        <Save size={18}/> {saving ? 'ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                    <Button onClick={() => { setIsEditing(false); window.location.reload(); }} variant="ghost" className="bg-white shadow-xl text-red-500 hover:bg-red-50">
                        <X size={18}/>
                    </Button>
                </>
            ) : (
                <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                    <Edit size={18}/> ویرایش صفحه
                </Button>
            )}
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#1e3a5f] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            <EditableContent 
                isEditing={isEditing}
                value={articles.hero.title}
                onChange={(val) => updateArticlesContent('hero', 'title', val)}
                className="text-center bg-transparent border-white/20"
            />
          </h1>
          <div className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
            <EditableContent 
                isEditing={isEditing}
                type="textarea"
                value={articles.hero.subtitle}
                onChange={(val) => updateArticlesContent('hero', 'subtitle', val)}
                className="text-center bg-transparent border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            <div className="flex-1 w-full relative">
              <Search className="absolute right-4 top-3.5 text-gray-400" size={20} />
              <input 
                type="text" 
                placeholder="جستجو در مقالات..." 
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl pr-12 pl-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {articles.categories.map((cat) => (
                <button 
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${selectedCategory === cat ? 'bg-[#1e3a5f] text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, idx) => (
              <article key={article.id} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all group flex flex-col h-full">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-lg text-xs font-bold text-[#1e3a5f]">
                    {article.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1"><Calendar size={14} /> {article.date}</span>
                    <span className="flex items-center gap-1"><Clock size={14} /> {article.readTime}</span>
                    <span className="flex items-center gap-1"><Eye size={14} /> {article.views}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1e3a5f] transition-colors leading-tight">
                    <EditableContent 
                        isEditing={isEditing}
                        value={article.title}
                        onChange={(val) => updateArticleItem(idx, 'title', val)}
                    />
                  </h3>
                  <div className="text-gray-500 text-sm leading-relaxed mb-6 line-clamp-3">
                    <EditableContent 
                        isEditing={isEditing}
                        type="textarea"
                        value={article.excerpt}
                        onChange={(val) => updateArticleItem(idx, 'excerpt', val)}
                    />
                  </div>
                  <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                        <User size={16} />
                      </div>
                      <span className="text-xs font-bold text-gray-700">{article.author}</span>
                    </div>
                    <button className="text-[#1e3a5f] font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                      ادامه مطلب <ArrowLeft size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
              <Search size={40} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">مقاله مورد نظر یافت نشد</h3>
            <p className="text-gray-500">لطفاً کلمات کلیدی دیگری را جستجو کنید.</p>
          </div>
        )}
      </section>

      {/* Newsletter */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="bg-[#1e3a5f] p-10 rounded-3xl shadow-xl text-center text-white">
          <h2 className="text-2xl font-bold mb-4">عضویت در خبرنامه تخصصی</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            با عضویت در خبرنامه، آخرین مقالات، اخبار و فرصت‌های سرمایه‌گذاری را مستقیماً در ایمیل خود دریافت کنید.
          </p>
          <div className="flex flex-col sm:flex-row max-w-md mx-auto gap-3">
            <input 
              type="email" 
              placeholder="آدرس ایمیل شما..." 
              className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 outline-none focus:bg-white/20 transition-all text-white placeholder:text-blue-200"
            />
            <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-8 font-bold">
              عضویت
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticlesPage;
